import { useLenis } from 'lenis/react';
import { useLayoutEffect, useRef, useState } from 'react';

interface UseLenisElementPositionOptions {
  mode: 'px' | 'percent';
  absolute?: boolean;
  bounded?: boolean;
}

export const useLenisElementPosition = (
  ref: React.RefObject<HTMLElement | null>,
  options: UseLenisElementPositionOptions = {
    mode: 'px',
    absolute: false,
    bounded: false,
  }
) => {
  const elementDocumentOffset = useRef<number>(0);
  const elementHeight = useRef<number>(0);

  const [viewportY, setViewportY] = useState<number>(0);

  const lenis = useLenis((lenis) => {
    let next: number;

    if (options.mode === 'percent') {
      next =
        (elementDocumentOffset.current - lenis.scroll) / elementHeight.current;
    } else {
      next = elementDocumentOffset.current - lenis.scroll;
    }

    if (options.absolute) {
      next = Math.abs(next);
    }

    if (options.bounded) {
      if (options.mode === 'percent') {
        next = Math.max(options.absolute ? 0 : -1, Math.min(next, 1));
      } else {
        next = Math.max(
          options.absolute ? 0 : -elementHeight.current,
          Math.min(next, elementHeight.current)
        );
      }
    }

    setViewportY(next);
  });

  useLayoutEffect(() => {
    if (!ref.current || !lenis) {
      return;
    }

    const { top, height } = ref.current.getBoundingClientRect();

    elementDocumentOffset.current = top + lenis.scroll;
    elementHeight.current = height;

    setViewportY(top);
  }, [lenis, ref]);

  return viewportY;
};
